// The bottom of the screen: the cards you hold, and the line above them that
// says why the last click did nothing (PLAN-UI §28.3, U7).
//
// One file because the pill stands clear of the hand and cannot be drawn
// without knowing whether there are cards in it — see `clear` below.
//
// Reads `SessionView.hand` and `SessionView.refusal` and hands a card back as
// its option (PLAN-CLIENT-SPLIT day 8): what is in the hand, which card is
// armed and where an artifact is lying are the engine's to say.

import { Hand } from "../HandFan";
import { HAND_REACH } from "../hand";
import { RefusalPill } from "../RefusalPill";
import { TrophyChooser } from "../TrophyChooser";
import type { Tool } from "../tool";
import { useSession, useSessionView } from "../useSessionView";

export function HandZone({
  tool,
  notice
}: {
  /** What a drag does, so the pill can say so once on a change. */
  tool: Tool;
  /** A sentence the game owes the player, said once (§29 U5). */
  notice?: string | null;
  /** Gone (day 8): the zone reads the session itself. Accepted so the shell keeps compiling. */
  board_?: unknown;
}) {
  const session = useSession();
  const { hand, refusal } = useSessionView();
  // Null outside a run and while watching: outside a run there is no deck at
  // all, and a spectator holds no seat to play a card from — the whole-seat
  // exception the transport takes. The engine makes both calls.
  const drawn = hand !== null;

  return (
    <>
      {/* The bottom-centre line (PLAN-UI §28.3, U7): why the last click did
          nothing, and — once, on a change — what the tool you just picked up
          does with a drag. It replaced `ShelfStrip`'s 340px caption line,
          whose ellipsis cut the long refusals in half.

          **Always**, watching included: a spectator still picks up the camera,
          and a refusal is a fact about a click rather than about a seat. It
          takes no pointer, so it can float over the board without being in
          the way of it. */}
      <RefusalPill
        // **Clear of a *raised* card, which the inset cannot say.** The hand
        // registers the fan at rest; a card lifted under the pointer stands
        // ~96px higher and covered the pill entirely. Same condition the
        // `<Hand/>` below is drawn on — with cards in it, since an empty hand
        // draws nothing — so the pill drops back to the edge the moment there
        // is no card to clear.
        clear={drawn && hand.cards.length > 0 ? HAND_REACH : 0}
        refusal={refusal.text}
        said={refusal.said}
        tool={tool}
        notice={notice}
      />

      {/* **Out of the shelf, because a card is a thing you hold.** In the dock
          they were a wrapped list under a YOUR HAND heading and read as more
          interface; here they fan off the bottom edge and the one part of the
          game that is literally a hand of cards looks like one. The loose
          artifact sentence came with them — the relocation map puts it in this
          zone, and it is the thing that fills the hand. */}
      {drawn && <Hand view={hand} onChoose={(option) => session.choose(option)} />}

      {/* A fallen Monarch's trophy (§31 A2): one card of three, or score. */}
      {drawn && (
        <TrophyChooser trophies={hand.trophies} onChoose={(option) => session.choose(option)} />
      )}
    </>
  );
}
